// PDF de muchos equipos (14-oct-2026): después del resumen, una hoja por equipo con sus datos,
// lo que se hizo y las fotos en grilla 2x2 (antes / después arriba, las 2 extras abajo).
// Si alguien del cliente quiere mostrar solo un equipo, imprime esa hoja.
import { rotulosPdf } from '../rotulosFotos';
import { C, M, T, CONTENT_W } from './theme.js';
import { cargarFoto, fitEnCaja, sanitizarTexto } from './helpers.js';
import { dibujarHeaderMini } from './layout.js';

const SIN_SERIE = ['SIN-SN', 'MOSTRADOR'];

// "Acuña/9/Pasillo" → "Acuña · Piso 9 · Pasillo"; "Córdoba/1SS/UCO" → "Córdoba · 1° subsuelo · UCO".
// Así cargan la ubicación en el Güemes (edificio/piso/sector). Si viene otra cosa, queda como está.
const piso = (p) => {
    const s = String(p).trim();
    if (/^PB$/i.test(s)) return 'Planta baja';
    const ss = s.match(/^(\d+)\s*SS$/i);
    if (ss) return `${ss[1]}° subsuelo`;
    return /^\d+$/.test(s) ? `Piso ${s}` : s;
};
export function ubicacionLegible(item = {}) {
    const ubic = String(item.ubicacionEquipo || item.equipoUbicacion || '').trim();
    const partes = [];
    if (ubic && ubic.toUpperCase() !== 'MOSTRADOR') {
        const t = ubic.split('/').map(x => x.trim()).filter(Boolean);
        if (t.length >= 2 && t.length <= 4 && /^(PB|\d+\s*SS|\d+)$/i.test(t[1])) {
            partes.push(t[0], piso(t[1]), ...t.slice(2));
        } else partes.push(ubic);
    }
    if (item.equipoPiso) partes.push(piso(item.equipoPiso));
    if (item.equipoSector) partes.push(item.equipoSector);
    return [...new Set(partes)].join(' · ');
}

export const serieDe = (item) => (item.equipoSerial && !SIN_SERIE.includes(String(item.equipoSerial).toUpperCase()) ? item.equipoSerial : null);
export const fuentesFotos = (item) => [item.fotoAntes, item.fotoDespues, ...(item.fotosExtra || []).slice(0, 2)];
const tieneFotos = (item) => fuentesFotos(item).some(Boolean);

const Y_INI = 30;   // debajo de la cabecera chica nueva (18-oct-2026)
const GAP = 6;

function celda(doc, foto, etiqueta, x, y, w, h) {
    doc.setFontSize(T.xxs);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...C.grayText);
    doc.text(etiqueta, x, y + 3);
    const yi = y + 5;
    doc.setFillColor(...C.grayLight);
    doc.roundedRect(x, yi, w, h, 2, 2, 'F');
    if (foto) {
        const { w: fw, h: fh } = fitEnCaja(foto.w, foto.h, w, h);
        try { doc.addImage(foto.data, foto.format, x + (w - fw) / 2, yi + (h - fh) / 2, fw, fh); } catch { /* foto rota: queda la caja */ }
    } else {
        doc.setFontSize(T.xs);
        doc.setFont(undefined, 'normal');
        doc.text('Sin foto', x + w / 2, yi + h / 2, { align: 'center' });
    }
    doc.setDrawColor(...C.grayBorder);
    doc.setLineWidth(0.2);
    doc.roundedRect(x, yi, w, h, 2, 2, 'S');
}

export async function dibujarPaginasPorEquipo(doc, ticketItems, { nroDoc = null, tipoLabel = 'Detalle por equipo' } = {}) {
    const conFotos = ticketItems.map((it, i) => ({ it, n: i + 1 })).filter(x => tieneFotos(x.it));
    if (!conFotos.length) return;
    // Muchos equipos: fotos un poco más livianas para que el PDF se pueda mandar por WhatsApp
    const [maxPx, calidad] = ticketItems.length > 12 ? [700, 0.72] : [900, 0.78];
    const total = ticketItems.length;
    const pageH = doc.internal.pageSize.getHeight();
    const LIMITE = pageH - 20;

    for (const { it, n } of conFotos) {
        // De a un equipo: así no se cargan las 28 fotos en memoria a la vez
        const [antes, despues, f3, f4] = await Promise.all(fuentesFotos(it).map(f => (f ? cargarFoto(f, maxPx, calidad).catch(() => null) : null))); // eslint-disable-line no-await-in-loop
        doc.addPage();
        dibujarHeaderMini(doc, { tipoLabel: `${tipoLabel} · Equipo ${n} de ${total}`, nroDoc });
        let y = Y_INI;

        // Encabezado del equipo
        const serie = serieDe(it);
        const ubic = ubicacionLegible(it);
        const modelo = [it.marcaEquipo || it.equipoMarca, it.modeloEquipo || it.equipoModelo].filter(Boolean).join(' ');
        doc.setFillColor(...C.navy);
        doc.roundedRect(M, y, 11, 11, 2, 2, 'F');
        doc.setFontSize(T.md); doc.setFont(undefined, 'bold'); doc.setTextColor(...C.white);
        doc.text(String(n), M + 5.5, y + 7.3, { align: 'center' });
        doc.setTextColor(...C.navy); doc.setFontSize(T.lg);
        doc.text(serie ? `N/S ${serie}` : `Equipo ${n}`, M + 15, y + 5);
        doc.setFontSize(T.sm); doc.setFont(undefined, 'normal'); doc.setTextColor(...C.grayText);
        doc.text(doc.splitTextToSize([ubic, modelo].filter(Boolean).join('   ·   ') || ' ', CONTENT_W - 15)[0], M + 15, y + 10);
        y += 16;

        // Qué se hizo y repuestos
        const trabajo = doc.splitTextToSize(sanitizarTexto(it.trabajo || it.trabajoRealizado || '') || 'Sin detalle', CONTENT_W - 8).slice(0, 5);
        const reps = (it.repuestosUsados || []).map(r => `${r.cantidad || 1} × ${r.nombre}`).join('   ·   ');
        const repsL = reps ? doc.splitTextToSize(reps, CONTENT_W - 8).slice(0, 2) : [];
        const altoTxt = 8 + trabajo.length * 4.2 + (repsL.length ? 5 + repsL.length * 4 : 0);
        doc.setFillColor(...C.grayBg);
        doc.roundedRect(M, y, CONTENT_W, altoTxt, 2, 2, 'F');
        doc.setFontSize(T.label); doc.setFont(undefined, 'bold'); doc.setTextColor(...C.grayText);
        doc.text('TRABAJO REALIZADO', M + 4, y + 4.5);
        doc.setFontSize(T.sm); doc.setFont(undefined, 'normal'); doc.setTextColor(...C.dark);
        doc.text(trabajo, M + 4, y + 9);
        if (repsL.length) {
            const yr = y + 9 + trabajo.length * 4.2 + 1;
            doc.setFontSize(T.label); doc.setFont(undefined, 'bold'); doc.setTextColor(...C.grayText);
            doc.text('REPUESTOS', M + 4, yr);
            doc.setFontSize(T.sm); doc.setFont(undefined, 'normal'); doc.setTextColor(...C.dark);
            doc.text(repsL, M + 4, yr + 4);
        }
        y += altoTxt + 5;

        // Fotos 2x2: antes / después arriba; las extras abajo solo si hay
        const filas = (f3 || f4) ? 2 : 1;
        const w = (CONTENT_W - GAP) / 2;
        const h = Math.min(filas === 2 ? 100 : 150, (LIMITE - y) / filas - 5 - (filas === 2 ? GAP : 0));
        const lab = rotulosPdf(it);   // el rótulo de cada foto (18-oct-2026)
        celda(doc, antes, lab[0], M, y, w, h);
        celda(doc, despues, lab[1], M + w + GAP, y, w, h);
        if (filas === 2) {
            y += h + 5 + GAP;
            if (f3) celda(doc, f3, lab[2], M, y, w, h);
            if (f4) celda(doc, f4, lab[3], M + w + GAP, y, w, h);
        }
    }
}
